import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { useAwaitControl } from 'react-redux-await-control';
import { useDrawer } from '~/hooks/useDrawer';
import {
  getCommentTypes,
  getTicketDetails,
  getTicketResolutionReasons,
  getTicketResolvingAreas,
  getTicketStatus,
  updateTicket,
} from '~/store/tickets/actions';
import { getLoggedUser } from '~/store/user/actions';
import { getPersonDetail } from '~/store/people/actions';
import { TicketManagementPriorities } from '~/typings/enums/Ticket/TicketPriorities';
import { FileType } from '~/ui/components/Files/Files';
import { CommentTypeName } from '~/typings/entities/Ticket';
import { normalizeStatusName } from './whatsappTemplates';
import FormStep, { FormValues } from './steps/FormStep';
import WhatsAppStep from './steps/WhatsAppStep';
import ReclameAquiStep from './steps/ReclameAquiStep';
import RaReviewStep from './steps/RaReviewStep';
import StepIndicator from './components/StepIndicator';
import { findCommentTypeId, isReclameAquiChannel, COMMENT_TYPE, RA_STEPS, STANDARD_STEPS } from './constants';
import { TicketLoadingWrapper } from './ResolveTicket.styles';
import TicketDetailsLoading from '../TicketDetails/TicketDetailsLoading';

type Step = 'form' | 'whatsapp' | 'reclameaqui' | 'rareview';

type ResolveTicketProps = { id: string };

const RA_STEP_INDEX: Record<Step, number> = {
  form: 0,
  whatsapp: 1,
  reclameaqui: 2,
  rareview: 3,
};

// Standard flow has no Reclame Aqui step, so the review sits one position earlier.
const STANDARD_STEP_INDEX: Record<Step, number> = {
  form: 0,
  whatsapp: 1,
  reclameaqui: 1,
  rareview: 2,
};

const RA_STEP_TITLE: Partial<Record<Step, string>> = {
  form: 'Resolver ticket',
  whatsapp: 'Resposta ao cliente',
  reclameaqui: 'Resposta no Reclame Aqui',
  rareview: 'Revisão final',
};

const STANDARD_STEP_TITLE: Partial<Record<Step, string>> = {
  whatsapp: 'Resposta ao cliente',
  rareview: 'Revisão final',
};

function validateValue(value: any, last = false): any {
  if (Array.isArray(value)) {
    return value.length ? value[last ? value.length - 1 : 0] : null;
  }
  return value || null;
}

function ResolveTicket({ id }: ResolveTicketProps) {
  const { closeDrawer, setConfig } = useDrawer();

  const [step, setStep] = useState<Step>('form');
  const [formData, setFormData] = useState<FormValues | null>(null);
  const [agentComment, setAgentComment] = useState('');
  const [publicMessage, setPublicMessage] = useState('');
  const [reservedMessage, setReservedMessage] = useState('');
  const [confirmedPublic, setConfirmedPublic] = useState(false);
  const [justificationResolutionId, setJustificationResolutionId] = useState<any>(null);

  const getTicketResolvingAreasControl = useAwaitControl(getTicketResolvingAreas);
  const getTicketStatusControl = useAwaitControl(getTicketStatus);
  const getTicketResolutionReasonsControl = useAwaitControl(getTicketResolutionReasons);
  const getTicketDetailsControl = useAwaitControl(getTicketDetails);
  const getCommentTypesControl = useAwaitControl(getCommentTypes);
  const updateTicketControl = useAwaitControl(updateTicket);
  const loggedUserControl = useAwaitControl(getLoggedUser);
  const personDetailControl = useAwaitControl(getPersonDetail);

  const isLoading = getTicketDetailsControl.isRunning(id);
  const updateTicketLoading = updateTicketControl.isRunning();
  const successfullyUpdated = updateTicketControl.isSuccessful();
  const isLoadingResolvingAreas = getTicketResolvingAreasControl.isRunning();
  const isLoadingStatus = getTicketStatusControl.isRunning();
  const isLoadingResolutionReasons = getTicketResolutionReasonsControl.isRunning();

  const ticketResolvingAreas = getTicketResolvingAreasControl.result();
  const status = getTicketStatusControl.result();
  const resolutionReasons = getTicketResolutionReasonsControl.result();
  const ticketDetail = getTicketDetailsControl.result(id);
  const commentTypes = getCommentTypesControl.result();
  const loggedUser = loggedUserControl.result();
  const personDetail = personDetailControl.result();

  const clientName = ticketDetail?.personName || (ticketDetail?.companyId ? '' : personDetail?.name) || '';
  const isReclameAqui = useMemo(() => isReclameAquiChannel(ticketDetail?.serviceChannelId), [ticketDetail]);

  const selectedStatusName = useMemo(() => {
    if (!formData?.ticketStatusId) return '';
    return status?.find((s: any) => s.id === formData.ticketStatusId)?.name || '';
  }, [status, formData]);

  const normalizedStatus = useMemo(() => normalizeStatusName(selectedStatusName), [selectedStatusName]);
  // Required Reclame Aqui messages by status: Pendente Cliente requires the reserved message,
  // Concluído requires the public response.
  const reservedRequired = normalizedStatus === 'pendente cliente';
  const publicRequired = normalizedStatus === 'concluido';

  const hasFiles = useMemo(() => {
    const files = formData?.files as FileType[] | undefined;
    return Array.isArray(files) && files.length > 0;
  }, [formData]);

  const priorityLabel = useMemo(() => {
    if (!formData?.priorityTicket) return undefined;
    return TicketManagementPriorities[formData.priorityTicket as keyof typeof TicketManagementPriorities];
  }, [formData]);

  const resolvingAreaName = useMemo(() => {
    const areaId = validateValue(formData?.resolvingAreaId, true);
    return ticketResolvingAreas?.find((area: any) => area.id === areaId)?.name;
  }, [formData, ticketResolvingAreas]);

  const justificationName = useMemo(() => {
    const jid = validateValue(justificationResolutionId, true);
    return resolutionReasons?.find((reason: any) => reason.id === jid)?.justification;
  }, [justificationResolutionId, resolutionReasons]);

  const buildResolutionPayload = useCallback(
    (comment: any) => ({
      loggedUser: loggedUser.name,
      priorityTicket: formData?.priorityTicket,
      ticketStatusId: formData?.ticketStatusId,
      justificationResolutionId: validateValue(justificationResolutionId, true),
      resolvingAreaId: validateValue(formData?.resolvingAreaId, true),
      userCreated: loggedUser.name,
      userCreatedId: loggedUser.id,
      files: formData?.files,
      comment,
    }),
    [loggedUser, formData, justificationResolutionId],
  );

  // Builds a single typed comment, resolving its numeric type id from GET /comment-types so every
  // resolution flow (WhatsApp, public/reserved Reclame Aqui and the backoffice note) is saved the
  // same way. Returns undefined when the text is empty so it can be skipped.
  const buildComment = useCallback(
    (description: string, publicComment: boolean, typeName: CommentTypeName) => {
      const text = (description || '').trim();
      if (!text) return undefined;
      return {
        ticketId: id,
        authorName: loggedUser?.name,
        authorNameId: loggedUser?.id,
        description: text,
        publicComment,
        typeId: findCommentTypeId(commentTypes, typeName),
      };
    },
    [id, loggedUser, commentTypes],
  );

  // Typed comment list sent on Reclame Aqui resolutions: the private WhatsApp message, the public
  // response published on Reclame Aqui and the optional reserved (consumer-only) message.
  const buildReclameAquiComments = useCallback(
    () =>
      [
        buildComment(agentComment, false, COMMENT_TYPE.WHATSAPP),
        buildComment(publicMessage, true, COMMENT_TYPE.PUBLIC),
        buildComment(reservedMessage, false, COMMENT_TYPE.PRIVATE),
      ].filter(Boolean),
    [buildComment, agentComment, publicMessage, reservedMessage],
  );

  const handleFormReview = useCallback(
    (values: FormValues, nextStep: 'compose' | 'resolve') => {
      setFormData(values);
      setJustificationResolutionId(values.justificationResolutionId ?? null);

      if (nextStep === 'compose') {
        setStep('whatsapp');
        return;
      }

      if (!loggedUser) return;
      // Direct resolution from the form: the agent note is saved as a typed BACKOFFICE comment.
      const comment = buildComment(values.comment || '', true, COMMENT_TYPE.BACKOFFICE);

      updateTicketControl.start({
        ticketId: id,
        data: {
          loggedUser: loggedUser.name,
          priorityTicket: values.priorityTicket,
          ticketStatusId: values.ticketStatusId,
          justificationResolutionId: validateValue(values.justificationResolutionId ?? null, true),
          resolvingAreaId: validateValue(values.resolvingAreaId, true),
          userCreated: loggedUser.name,
          userCreatedId: loggedUser.id,
          files: values.files,
          comment,
        },
      });
    },
    [loggedUser, id, buildComment, updateTicketControl],
  );

  const handleBack = useCallback(() => {
    if (step === 'whatsapp') setStep('form');
    if (step === 'reclameaqui') setStep('whatsapp');
    if (step === 'rareview') setStep(isReclameAqui ? 'reclameaqui' : 'whatsapp');
  }, [step, isReclameAqui]);

  const handleResolve = useCallback(() => {
    if (!formData || !loggedUser) return;

    if (isReclameAqui) {
      updateTicketControl.start({ ticketId: id, data: buildResolutionPayload(buildReclameAquiComments()) });
      return;
    }

    // Standard flow: the WhatsApp message to the client is saved as a typed WHATSAPP comment.
    const comment = buildComment(agentComment, false, COMMENT_TYPE.WHATSAPP);
    updateTicketControl.start({ ticketId: id, data: buildResolutionPayload(comment) });
  }, [
    formData,
    loggedUser,
    id,
    isReclameAqui,
    agentComment,
    buildComment,
    buildResolutionPayload,
    buildReclameAquiComments,
  ]);

  useEffect(() => {
    const isFirstStep = step === 'form';
    const stepTitle = isReclameAqui ? RA_STEP_TITLE[step] : STANDARD_STEP_TITLE[step];
    setConfig('resolve-ticket', {
      backButton: !isFirstStep,
      onBackClick: isFirstStep ? undefined : handleBack,
      ...(stepTitle ? { title: stepTitle } : {}),
    });
  }, [step, handleBack, isReclameAqui]);

  useEffect(() => {
    if (successfullyUpdated) {
      closeDrawer('resolve-ticket');
    }
  }, [successfullyUpdated]);

  useEffect(() => {
    setStep('form');
    setFormData(null);
    setAgentComment('');
    setPublicMessage('');
    setReservedMessage('');
    setConfirmedPublic(false);
    setJustificationResolutionId(null);
  }, [id]);

  useEffect(() => {
    // compara como string: a API devolve id numérico e o prop pode vir string —
    // com !== estrito a condição nunca fecha e o fetch entra em loop
    if (String(id) !== String(ticketDetail?.id ?? '')) {
      getTicketDetailsControl.start({ id }, { actionId: id });
    }
  }, [id, ticketDetail]);

  useEffect(() => {
    if (ticketDetail?.personId && !ticketDetail?.personName) {
      personDetailControl.start({ id: ticketDetail.personId });
    }
  }, [ticketDetail?.personId, ticketDetail?.personName]);

  useEffect(() => {
    if (!status?.length) getTicketStatusControl.start();
    if (!resolutionReasons?.length) getTicketResolutionReasonsControl.start();
    if (!ticketResolvingAreas?.length) getTicketResolvingAreasControl.start();
    if (!commentTypes?.length) getCommentTypesControl.start();
  }, [status?.length, resolutionReasons?.length, ticketResolvingAreas?.length, commentTypes?.length]);

  useEffect(
    () => () => {
      updateTicketControl.clear();
    },
    [],
  );

  if (isLoading) {
    return (
      <TicketLoadingWrapper>
        <TicketDetailsLoading />
      </TicketLoadingWrapper>
    );
  }

  const statusNameDisplay = selectedStatusName || undefined;

  const renderStep = () => {
    if (step === 'form') {
      return (
        <FormStep
          id={id}
          status={status || []}
          resolvingAreas={ticketResolvingAreas || []}
          ticketDetail={ticketDetail}
          isLoadingResolvingAreas={isLoadingResolvingAreas}
          isLoadingStatus={isLoadingStatus}
          resolutionReasons={resolutionReasons || []}
          isLoadingResolutionReasons={isLoadingResolutionReasons}
          isResolving={updateTicketLoading}
          onReview={handleFormReview}
        />
      );
    }

    if (step === 'whatsapp') {
      return (
        <WhatsAppStep
          ticketId={String(id)}
          clientName={clientName}
          normalizedStatusName={normalizedStatus}
          hasFiles={hasFiles}
          agentComment={agentComment}
          onCommentChange={setAgentComment}
          onNext={() => setStep(isReclameAqui ? 'reclameaqui' : 'rareview')}
          nextHint={isReclameAqui ? undefined : 'Próximo: revisão final.'}
        />
      );
    }

    if (step === 'reclameaqui') {
      return (
        <ReclameAquiStep
          publicMessage={publicMessage}
          reservedMessage={reservedMessage}
          confirmed={confirmedPublic}
          publicRequired={publicRequired}
          reservedRequired={reservedRequired}
          onPublicChange={(value) => {
            setPublicMessage(value);
            if (!value.trim()) setConfirmedPublic(false);
          }}
          onReservedChange={setReservedMessage}
          onConfirmChange={setConfirmedPublic}
          onReview={() => setStep('rareview')}
        />
      );
    }

    return (
      <RaReviewStep
        ticketId={String(id)}
        clientName={clientName}
        normalizedStatusName={normalizedStatus}
        hasFiles={hasFiles}
        agentComment={agentComment}
        resolvingAreaName={resolvingAreaName}
        priorityLabel={priorityLabel}
        statusName={statusNameDisplay}
        justificationText={justificationName}
        publicMessage={publicMessage}
        reservedMessage={reservedMessage}
        files={formData?.files as FileType[]}
        onResolve={handleResolve}
        isLoading={updateTicketLoading}
        isReclameAqui={isReclameAqui}
      />
    );
  };

  // The step indicator is shown for the whole Reclame Aqui flow and, for standard tickets, only
  // once the messaging flow starts (WhatsApp / review) — simple direct resolves keep the plain form.
  const showStepIndicator = isReclameAqui || step === 'whatsapp' || step === 'rareview';
  const indicatorSteps = isReclameAqui ? RA_STEPS : STANDARD_STEPS;
  const indicatorIndex = isReclameAqui ? RA_STEP_INDEX[step] : STANDARD_STEP_INDEX[step];

  return (
    <>
      {showStepIndicator && <StepIndicator steps={indicatorSteps} current={indicatorIndex} />}
      {renderStep()}
    </>
  );
}

export default ResolveTicket;
